/**
 * useToast Hook
 * Manages toast notifications throughout the application
 */

import { useCallback, useState } from 'react';

let toastId = 0;

export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((notification) => {
    const id = toastId++;
    const newToast = {
      id,
      type: 'success',
      duration: 5000,
      ...notification
    };

    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSuccess = useCallback((title, message, options = {}) => {
    return addToast({
      type: 'success',
      title,
      message,
      ...options
    });
  }, [addToast]);

  const showError = useCallback((title, message, options = {}) => {
    return addToast({
      type: 'error',
      title,
      message,
      duration: 7000,
      ...options
    });
  }, [addToast]);

  const showInfo = useCallback((title, message, options = {}) => {
    return addToast({
      type: 'info',
      title,
      message,
      ...options
    });
  }, [addToast]);

  const showAchievement = useCallback((achievement, options = {}) => {
    return addToast({
      type: 'achievement',
      title: `🎉 Achievement Unlocked!`,
      message: achievement,
      duration: 6000,
      ...options
    });
  }, [addToast]);

  const showStreak = useCallback((days, options = {}) => {
    return addToast({
      type: 'streak',
      title: `🔥 Streak Milestone!`,
      message: `You've maintained a ${days}-day learning streak!`,
      duration: 6000,
      ...options
    });
  }, [addToast]);

  const showRecommendation = useCallback((courseTitle, courseId, options = {}) => {
    return addToast({
      type: 'info',
      title: `💡 Recommended For You`,
      message: courseTitle,
      actionText: 'View Course',
      action: () => window.location.href = `/courses/${courseId}`,
      duration: 8000,
      ...options
    });
  }, [addToast]);

  return {
    toasts,
    addToast,
    removeToast,
    showSuccess,
    showError,
    showInfo,
    showAchievement,
    showStreak,
    showRecommendation
  };
};

export default useToast;
